import {
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemClock16 from "@traveloka/icon-kit-web/react/IcSystemClock16";
import { useMemo } from "react";
import { StyleSheet } from "react-native";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../../utils";
import FlightFacilitiesInfo from "../FlightFacilitiesInfo";
import { FlightTransitInfo } from "../FlightTransitInfo";
import {
  FlightDetailComponentProps,
  FlightDetailItemComponentProps,
} from "../types";

/**
 * FlightDetail component to show flight details information
 * Mapped all data into flight detail item component
 */
type FlightDetailsComponent = React.FC<FlightDetailComponentProps> & {
  Item: typeof FlightDetailItem;
};

const FlightDetails: FlightDetailsComponent = ({
  arrivalAirportCity,
  departureAirportCity,
  totalDuration,
  journeys,
}) => {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  return (
    <BMView spacing="md">
      <BMView style={styles.header} row align="center" justify="between">
        <BMText variant="titleNormal">
          {departureAirportCity} → {arrivalAirportCity}
        </BMText>
        <BMView row align="center" spacing="xs">
          <IcSystemClock16 color={BMComponents.BMStyleIconInkSecondary} />
          <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
            {totalDuration}
          </BMText>
        </BMView>
      </BMView>
      {journeys
        .flatMap((j) => j.segments)
        .map((segment, index) => (
          <FlightDetailItem key={index} {...segment} />
        ))}
    </BMView>
  );
};

/**
 * Expose FlightDetailItem as a static property of FlightDetail
 * so it can be accessed as FlightDetail.Item
 */
FlightDetails.Item = FlightDetailItem;

/**
 * FlightDetailItem component to show flight detail item information
 * including departure, arrival, duration, airlines, seat class, and stop info
 */
function FlightDetailItem({
  airlines,
  departureDetail,
  arrivalDetail,
  duration,
  seatClass,
  stopInfo,
  transitInfo,
}: FlightDetailItemComponentProps) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const { CorporateFlightProductSummary: cr } = useMobileContentResource();
  return (
    <BMView spacing="md">
      {transitInfo ? (
        <BMView style={styles.paddingHorizontalMd}>
          <FlightTransitInfo {...transitInfo} />
        </BMView>
      ) : null}
      <BMView row spacing="lg" style={styles.container}>
        <BMView justify="between" align="center" style={styles.paddingBottomLg}>
          <BMView align="end">
            <BMText variant="uiHuge">{departureDetail.departureTime}</BMText>
            <BMText variant="uiNormal" ink="BMStyleTextInkSecondary">
              {departureDetail.departureDate}
            </BMText>
          </BMView>
          <BMText variant="uiNormal" ink="BMStyleTextInkSecondary">
            {duration}
          </BMText>
          <BMView align="end">
            <BMText variant="uiHuge">{arrivalDetail.arrivalTime}</BMText>
            <BMText variant="uiNormal" ink="BMStyleTextInkSecondary">
              {arrivalDetail.arrivalDate}
            </BMText>
          </BMView>
        </BMView>
        <BMView style={styles.paddingBottomXl2} align="center">
          <BMView style={styles.indicator} />
          <BMView flex style={styles.durationIndicator} />
          <BMView style={[styles.indicator, styles.arrivalIndicator]} />
        </BMView>
        <BMView flex spacing="md">
          <BMView>
            <BMText variant="titleSmall">
              {departureDetail.airport.city} (
              {departureDetail.airport.airportCode})
            </BMText>
            <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
              {departureDetail.airport.internationalAirportName}
            </BMText>
            {departureDetail.departureTerminal ? (
              <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
                {formatMessage(cr.flightDepartureArrivalTerminal, {
                  num: departureDetail.departureTerminal,
                })}
              </BMText>
            ) : null}
          </BMView>
          <FlightFacilitiesInfo airlines={airlines} seatClass={seatClass} />
          {stopInfo ? (
            <FlightTransitInfo
              internationalAirportName={stopInfo.internationalAirportName}
              duration={stopInfo.duration}
            />
          ) : null}
          <BMView>
            <BMText variant="titleSmall">
              {arrivalDetail.airport.city} ({arrivalDetail.airport.airportCode})
            </BMText>
            <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
              {arrivalDetail.airport.internationalAirportName}
            </BMText>
            {arrivalDetail.arrivalTerminal ? (
              <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
                {formatMessage(cr.flightDepartureArrivalTerminal, {
                  num: arrivalDetail.arrivalTerminal,
                })}
              </BMText>
            ) : null}
          </BMView>
        </BMView>
      </BMView>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    header: {
      paddingVertical: BMPrimitives.BMSize.lg,
      paddingHorizontal: BMPrimitives.BMSize.md,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorDisabled,
    },
    paddingHorizontalMd: {
      paddingHorizontal: BMPrimitives.BMSize.md,
    },
    container: {
      padding: BMPrimitives.BMSize.md,
    },
    paddingBottomLg: {
      paddingBottom: BMPrimitives.BMSize.lg,
    },
    paddingBottomXl2: {
      paddingBottom: BMPrimitives.BMSize.xl2,
    },
    indicator: {
      width: BMPrimitives.BMSize.md,
      height: BMPrimitives.BMSize.md,
      borderRadius: BMPrimitives.BMRadius.rounded,
      marginVertical: BMPrimitives.BMSize.xs,
      borderWidth: 1.5,
      borderColor: BMComponents.BMStyleSurfaceBgcolorHero,
    },
    durationIndicator: {
      borderLeftWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorBold,
    },
    arrivalIndicator: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorHero,
    },
  });
}

export default FlightDetails;
